// Pipeline (PL) quantity-tracking module registry.
//
// Mirrors the "For AI" tab of the Pipeline Tracker Template workbook: each
// trackable cost code maps to an input module (the archetype of its tracker
// sheet), a unit of measure, and whether quantities break out by pipe size.
// Cost codes the workbook marks "Not in tracker" are simply absent — selecting
// them on the Work tab shows nothing on the Quantities step.
//
// Module archetypes (what the field actually types each day):
//   stations — initial/final station pairs (STA+00), footage computed
//   count    — one installed-quantity number in the code's UOM
//   tests    — test packages completed
//   ndt      — welds rejected / repaired / cut out
//   mats     — mat movements (install / remove / relocate / trash)
//   weather  — rain day + rainfall inches
//   crewDays — crew days only (DY / LS support codes)
//
// Facilities (FAC) and E&I get their own registries later; entry point is
// moduleForCode(code) which future registries extend by work type.

// ── Station math ────────────────────────────────────────────────────────────
// Stations read "420+00" = station 420 + 00 ft = 42,000 ft. Accepts bare
// numbers ("19+67" → 1,967; "1250" → 1,250 ft).
export const parseStation = (s) => {
    const t = String(s ?? '').trim().replace(/\s/g, '');
    if (!t) return null;
    const m = t.match(/^(\d+)\+(\d+(?:\.\d+)?)$/);
    if (m) return parseInt(m[1], 10) * 100 + parseFloat(m[2]);
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : null;
};

export const formatStation = (ft) => {
    if (ft == null || !Number.isFinite(ft)) return '';
    const sta = Math.floor(ft / 100);
    const rem = Math.round((ft - sta * 100) * 100) / 100;
    return `${sta}+${rem < 10 ? '0' : ''}${rem}`;
};

// Footage of one initial/final pair; crews can work up- or down-station, so
// direction doesn't matter.
export const pairFootage = (pair) => {
    const a = parseStation(pair?.from);
    const b = parseStation(pair?.to);
    if (a == null || b == null) return 0;
    return Math.abs(b - a);
};

// ── Registry (from the "For AI" tab; PL cost codes only) ────────────────────
// pipeSize: true = the tracker breaks Today/To-Date out by pipe size.
// welds: true = the sheet also takes a daily weld/joint count.
const M = (module, desc, uom, opts = {}) => ({ module, desc, uom, ...opts });

export const PL_QTY_MODULES = {
    // Project support
    '100101': M('crewDays', 'Project Team', 'DY'),
    '101001': M('weather', 'Weather', 'DY'),
    // Mob / Demob
    '100403': M('crewDays', 'Mob / Demob', 'LS'),
    '100401': M('crewDays', 'Mobilization', 'LS'),
    '100402': M('crewDays', 'Demobilization', 'LS'),
    // Site prep
    '140403': M('mats', 'Mat Install', 'EA', { matsMode: 'install' }),
    '140404': M('mats', 'Mat Removal', 'EA', { matsMode: 'remove' }),
    '140103': M('count', 'Fence Gaps / Goalposts', 'EA'),
    '140102': M('stations', 'Clear ROW', 'LF'),
    '140104': M('stations', 'Grade ROW', 'LF'),
    '140105': M('stations', 'Clean-up', 'LF'),
    '142001': M('stations', 'ROW Restoration', 'LF'),
    // Pipe
    '140602': M('stations', 'Skids', 'LF'),
    '140601': M('stations', 'Stringing CS Pipe', 'LF', { pipeSize: true }),
    '140606': M('stations', 'Stringing Composite Pipe', 'LF', { pipeSize: true }),
    '140701': M('stations', 'Bending / Engineering', 'LF', { pipeSize: true }),
    '140801': M('stations', 'Lay and Weld CS', 'LF', { pipeSize: true, welds: true }),
    '141001': M('stations', 'Lay and Fuse HDPE', 'LF', { pipeSize: true, welds: true, weldLabel: 'Fusions today' }),
    '141101': M('stations', 'Lay and Join Fiberglass Pipe', 'LF', { pipeSize: true, welds: true, weldLabel: 'Joints today' }),
    '142201': M('stations', 'Lay and Join Composite Pipe', 'LF', { pipeSize: true, welds: true, weldLabel: 'Joints today' }),
    '141301': M('stations', 'Lower In', 'LF', { pipeSize: true }),
    '141601': M('ndt', 'NDE', 'EA', { pipeSize: true }),
    // Fab weld — simplified to weld inches per day (no per-activity ROC in v1).
    '142101': M('count', 'Fab Weld / Pipe Install CS', 'WI', { pipeSize: true, qtyLabel: 'Weld inches today' }),
    '142110': M('count', 'Fab Weld / Pipe Install SS', 'WI', { pipeSize: true, qtyLabel: 'Weld inches today' }),
    // Paint / coating
    '142109': M('count', 'Paint', 'SF', { pipeSize: true }),
    '141201': M('stations', 'Coating', 'LF', { pipeSize: true }),
    // Dirtwork
    '140201': M('stations', 'Ditching', 'LF', { pipeSize: true }),
    '141303': M('stations', 'Backfill', 'LF', { pipeSize: true }),
    // Tie-ins
    '141507': M('count', 'Tie-ins', 'EA', { pipeSize: true, welds: true }),
    '141501': M('count', 'Tie-ins CS', 'EA', { pipeSize: true, welds: true }),
    '141504': M('count', 'Tie-ins HDPE', 'EA', { pipeSize: true, welds: true }),
    '141505': M('count', 'Tie-ins Fiberglass', 'EA', { pipeSize: true, welds: true }),
    '141506': M('count', 'Tie-ins Composite Pipe', 'EA', { pipeSize: true, welds: true }),
    '142004': M('count', 'Cathodic Test Stations', 'EA', { pipeSize: true }),
    // HDD / Bore — simplified to daily footage (no per-crossing activity % in v1).
    '141903': M('count', 'HDD', 'LF', { pipeSize: true }),
    '141803': M('count', 'Bore', 'LF', { pipeSize: true }),
    // Testing
    '141710': M('tests', 'Testing', 'PK', { pipeSize: true }),
    '141701': M('tests', 'Test CS', 'PK', { pipeSize: true }),
    '141707': M('tests', 'Test HDPE', 'PK', { pipeSize: true }),
    '141708': M('tests', 'Test Fiberglass', 'PK', { pipeSize: true }),
    '141709': M('tests', 'Test Composite Pipe', 'PK', { pipeSize: true }),
    // Concrete
    '150101': M('count', 'Concrete', 'CY', { qtyLabel: 'Cubic yards placed today' }),
};

// Codes the "For AI" tab explicitly marks "Not in tracker" — no quantity card,
// ever (support/overhead codes with nothing measurable to enter daily).
const NOT_TRACKED = new Set([
    '100109', '100201', '100303', '100503', '100901', '170101', '142301', // project support
    '140108', '130301', '140101', '130601',                              // site prep
    '142711', '140604', '140605', '140206',                              // pipe / dirtwork rollups
    '141911', '141203', '142108', '120101', '142107',                    // hdd/bore, paint/coat, steel, E&I, building
    '160402', '142701', '195350',                                        // pre-comm, composite, T&M
]);

// Jobs often budget under codes the tracker workbook doesn't list (client WBS,
// 160xxx above-ground codes…). Rather than show nothing, infer the module from
// the phase description; anything unrecognized gets a plain quantity card.
//
// pipeSize follows the registry's own rule rather than the code's origin: work
// done *to the pipe* breaks out by size, ROW and site work doesn't. Without it
// an inferred code like "160321 Pipe Handling / Install" — exactly the phase a
// crew runs three sizes through in a day — would take one lumped number.
const INFERRED = [
    [/tie.?in|weld/i, (d) => M('count', d, 'EA', { welds: true, pipeSize: true })],
    [/paint|coat/i, (d) => M('count', d, 'SF', { pipeSize: true })],
    [/ditch|backfill|lower.?in|\bstring|\blay\b|bend/i, (d) => M('stations', d, 'LF', { pipeSize: true })],
    [/grade|clear|clean.?up|restor|\brow\b|skid/i, (d) => M('stations', d, 'LF')],
    [/hydro|test/i, (d) => M('tests', d, 'PK', { pipeSize: true })],
    [/\bnde\b|\bndt\b|x.?ray/i, (d) => M('ndt', d, 'EA', { pipeSize: true })],
    [/\bmats?\b/i, (d) => M('mats', d, 'EA', { matsMode: 'install' })],
    [/hdd|bore|drill/i, (d) => M('count', d, 'LF', { pipeSize: true })],
    [/concrete|civil/i, (d) => M('count', d, 'CY', { qtyLabel: 'Cubic yards placed today' })],
    [/mob\b|demob/i, (d) => M('crewDays', d, 'LS')],
    [/pipe|install/i, (d) => M('stations', d, 'LF', { pipeSize: true })],
];

export const moduleForCode = (code, desc) => {
    const c = String(code || '').trim();
    const exact = PL_QTY_MODULES[c];
    if (exact) return exact;
    if (NOT_TRACKED.has(c)) return null;
    const d = String(desc || '').trim();
    if (!d) return null; // unknown code, no description to go on
    for (const [re, make] of INFERRED) {
        if (re.test(d)) return make(d);
    }
    return M('count', d, 'EA', { qtyLabel: 'Quantity installed today' });
};

// Pipeline job? Work_Type on PCV.Form_Job_Master rides in the legacy
// Cat_Code_BU_18 slot; fall back to the derived work-type list (jobWorkTypes)
// whose first entry is the dominant discipline.
const PL_WORK_TYPES = new Set(['PIP', 'CAP', 'NPC', 'PRM', 'PRN', 'M&I', 'INT']);
export const isPipelineJob = (jobData, workTypes) => {
    const code = String(jobData?.Cat_Code_BU_18 || '').trim().toUpperCase();
    if (PL_WORK_TYPES.has(code)) return true;
    const first = String(workTypes?.[0] || '').toLowerCase();
    return first.includes('pipeline');
};

// ── Daily quantity roll-up ──────────────────────────────────────────────────
// A qtyDetail row is one cost code's inputs for the day:
//   { costCode, pairs: [{from,to,size}], qty: number|'' , qtyBySize: {size: qty},
//     crewDays, welds, testsToday, ndtRejected, ndtRepaired, ndtCutOuts,
//     rainDay, rainInches, matsMoved, matsTrashed, budgetQty, comment }
// installedToday() collapses that to the one number the flat
// PC.Field_Report_Quantity row (and % complete math) uses.
const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

export const installedToday = (row, mod) => {
    if (!mod) return num(row?.qty);
    if (mod.module === 'stations') return (row?.pairs || []).reduce((t, p) => t + pairFootage(p), 0);
    if (mod.module === 'tests') return num(row?.testsToday);
    if (mod.module === 'weather') return row?.rainDay === 'Y' ? 1 : 0;
    if (mod.module === 'crewDays') return num(row?.crewDays);
    if (mod.module === 'ndt') return num(row?.qty);
    if (row?.qtyBySize && Object.keys(row.qtyBySize).length) {
        return Object.values(row.qtyBySize).reduce((t, v) => t + num(v), 0);
    }
    return num(row?.qty);
};

// Per-size split for the flat rows (Task column carries the size label).
export const installedBySize = (row, mod) => {
    if (!mod?.pipeSize) return null;
    if (mod.module === 'stations') {
        const out = {};
        (row?.pairs || []).forEach((p) => {
            const size = String(p?.size || '').trim();
            if (!size) return;
            out[size] = (out[size] || 0) + pairFootage(p);
        });
        return Object.keys(out).length ? out : null;
    }
    const bySize = row?.qtyBySize || {};
    const out = {};
    Object.entries(bySize).forEach(([size, v]) => {
        if (String(v ?? '').trim() !== '') out[size] = num(v);
    });
    return Object.keys(out).length ? out : null;
};
